import { OrganizationType } from "@remi/enums/organization-type";

import { MaskedPhoneRow } from "~/components/ui/MaskedPhoneRow";
import { useBidsByOrganization, useOrganization } from "~/hooks/useOrganization";
import { usePerson } from "~/hooks/usePerson";
import { getPhoneRowLabel } from "../../contact-phone-label";
import { EmailContactRow, PhoneContactRow } from "./ContactRow";
import { HomeownerBidInfo } from "./HomeownerBidInfo";

interface ContactDetailsCardProps {
  personId: string;
  showButtons?: boolean;
}

export const ContactDetailsCard = ({ personId, showButtons = true }: ContactDetailsCardProps) => {
  const { data: person } = usePerson(personId);
  const { data: organization } = useOrganization(person?.organizationId);
  const { data: bids } = useBidsByOrganization(
    organization?.type === OrganizationType.HOMEOWNER ? organization.id : undefined,
  );

  if (!person) {
    return null;
  }

  const secondaryPhones = person.phones.filter((phone) => phone.id !== person.primaryPhoneId);

  return (
    <div className="flex w-full flex-col gap-2 p-4">
      <div className="flex w-full flex-col">
        {person.primaryPhone ? (
          <PhoneContactRow
            phone={person.primaryPhone.number}
            personId={personId}
            isPrimary={true}
            label={getPhoneRowLabel({ phoneNotes: person.primaryPhone.phoneNotes, isPrimary: true })}
            preferredContactMethod={person.contact.preferredContactMethod ?? undefined}
            className="h-10 rounded-lg px-2 font-normal text-xs hover:bg-background-primaryHover"
            showButtons={showButtons}
          />
        ) : null}
        {secondaryPhones.map((phone) => (
          <PhoneContactRow
            key={phone.id}
            phone={phone.number}
            personId={personId}
            isPrimary={false}
            label={getPhoneRowLabel({ phoneNotes: phone.phoneNotes, isPrimary: false })}
            preferredContactMethod={person.contact.preferredContactMethod ?? undefined}
            className="h-10 rounded-lg px-2 font-normal text-xs hover:bg-background-primaryHover"
            showButtons={showButtons}
          />
        ))}

        {Array.from({ length: person.withheldPhoneCount }, (_, index) => (
          <MaskedPhoneRow key={`withheld-${index}`} className="h-10 rounded-lg px-2" />
        ))}

        {person.primaryEmailAddress ? (
          <EmailContactRow
            email={person.primaryEmailAddress.emailAddress}
            isPrimary={true}
            preferredContactMethod={person.contact.preferredContactMethod ?? undefined}
            className="h-10 rounded-lg px-2 font-normal text-xs hover:bg-background-primaryHover"
            showButtons={showButtons}
          />
        ) : null}
        {person.emailAddresses
          .filter((email) => email.id !== person.primaryEmailAddressId)
          .map((email) => (
            <EmailContactRow
              key={email.id}
              email={email.emailAddress}
              isPrimary={false}
              preferredContactMethod={person.contact.preferredContactMethod ?? undefined}
              className="h-10 rounded-lg px-2 font-normal text-xs hover:bg-background-primaryHover"
              showButtons={showButtons}
            />
          ))}
      </div>
      {bids && bids.length > 0 && bids.map((bid) => <HomeownerBidInfo key={bid.id} bidId={bid.id} />)}
    </div>
  );
};
